import { playerSearchWithMetaRatings } from "@/api/apiFc24";
import Button from "@/components/Button";
import Divider from "@/components/atoms/Divider/Divider";
import { RangeFilter } from "@/components/molecules/Filters/RangeFilter";
import { NowImporting } from "@/components/molecules/NowImporting/NowImporting";
import SectionList from "@/components/organisms/SBCFiltersSidebar/SideBarComponents/SectionListComponent";
import SbcSidePanel from "@/containers/SbcSidePanel";
import {
  importTutorialArrow,
  roundGreenCheckmarkIcon,
  roundRedMinusIcon,
} from "@/designSystemIcons";
import { fireGTMEvent } from "@/functions/fireGTMEvent";
import { useInitImport } from "@/hooks/useInitImport";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import useSearch from "@/hooks/useSearch";
import { cn } from "@/lib/utils";
import GetCardSBC from "@/pages/Sbcs/SbcSetPage/components/GetCardSbc";
import { setIsCurrentlyImporting } from "@/redux/globalState/globalStateSlice";
import { showModal } from "@/redux/message/messageSlice";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { PlayersFrom } from "@/redux/players/playersSlice";
import {
  addExcludedPlayer,
  addIncludedPlayer,
  removeExcludedPlayer,
  removeIncludedPlayer,
  sbcFiltersSelector,
  updateExcludeActiveSquad,
  updateExcludeObjectivePlayers,
  updateExcludeSbcPlayers,
  updateIncludePlayersFrom,
  updateIncludeTransferlist,
  updateMaxRating,
  updateMinRating,
  updatePrioritizeDuplicates,
  updatePrioritizeSbcStorage,
  updateUseUntradeablesOnly,
} from "@/redux/sbcs/sbcFiltersSlice";
import { Sbc, getSbcsSelector } from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import FiltersActive from "../../molecules/FiltersActive/FiltersActive";
import { FiltersPlayersFrom } from "../../molecules/FiltersPlayersFrom/FiltersPlayersFrom";
import { FiltersSidebarPlayerSlot } from "../../molecules/FiltersSidebarPlayerSlot/FiltersSidebarPlayerSlot";
import IncludeExcludeSection from "./SideBarComponents/IncludeExcludeSection";
import IncludedExcludedPlayers from "./SideBarComponents/IncludedExcludedPlayers";
import SectionToggle from "./SideBarComponents/SectionToggle";

type Props = {
  isMobile?: boolean;
  onHandleImportClicked: () => void;
  onHandleSolveAgain?: () => void;
  onHandleRemoveAndCompletePlayers?: () => void;
  sbc?: Sbc;
  rewardImageUrl?: string;
  isUsingClubImport: boolean;
  showFiltersBar?: boolean;
  overflowXTrue?: boolean;
  isOnSolutionsViewPage?: boolean;
  showReward?: boolean;
};
const SBCFiltersSidebar: React.FC<Props> = ({
  overflowXTrue,
  isOnSolutionsViewPage,
  sbc,
  showReward,
}) => {
  const {
    playersFrom,
    prioritizeDuplicates,
    includeTransferlist,
    prioritizeSbcStorage,
    useUntradeablesOnly,
    excludeActiveSquad,
    excludeSbcPlayers,
    excludeObjectivePlayers,
    includedPlayers,
    excludedPlayers,
    minRating,
    maxRating,
  } = useSelector(sbcFiltersSelector);
  const { user } = useSelector(getUserSelector);
  const { sbcs } = useSelector(getSbcsSelector);
  const dispatch = useDispatch<AppDispatch>();
  const [showSearch, setShowSearch] = useState<
    "include" | "exclude" | undefined
  >();
  const [showAllSectionType, setShowAllSectionType] = useState<
    "include" | "exclude" | null
  >(null);
  const showAllSection = showAllSectionType !== null;
  const [showAllIncluded, setShowAllIncluded] = useState(false);
  const [showAllExcluded, setShowAllExcluded] = useState(false);
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const navigate = useNavigate();
  useEscape(() => showSearch && setShowSearch(undefined));
  const initImport = useInitImport({ user });
  const [showImporting, setShowImporting] = useState(false);
  const [searchTerm, setSearchTerm, isSearching, result] = useSearch(
    "",
    (name: string, page: number) =>
      playerSearchWithMetaRatings(name, page, user.platform || "Playstation"),
  ) as [
    string,
    React.Dispatch<React.SetStateAction<string>>,
    boolean,
    SearchedPlayer[],
  ];

  const handleClickedPlayerSlot = (
    type: "include" | "exclude",
    resourceId?: number,
  ) =>
    resourceId
      ? dispatch(
          type === "include"
            ? removeIncludedPlayer(resourceId)
            : removeExcludedPlayer(resourceId),
        )
      : setShowSearch(type);

  const handleAddPlayer = (player: SearchedPlayer) => {
    setSearchTerm("");
    if (showSearch === "include") {
      fireGTMEvent("clicked_include_player");
      dispatch(addIncludedPlayer(player));
    } else {
      fireGTMEvent("clicked_exclude_player");
      dispatch(addExcludedPlayer(player));
    }
    setShowSearch(undefined);
  };

  const includedPlayerSlots = includedPlayers.map((p) => (
    <FiltersSidebarPlayerSlot
      key={p.resourceId}
      onClick={() => handleClickedPlayerSlot("include", p.resourceId)}
      player={p}
    />
  ));

  includedPlayerSlots.push(
    <div className="relative" key="include-add">
      <FiltersSidebarPlayerSlot
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!user.uuid && includedPlayerSlots.length === 1) {
            dispatch(
              showModal({
                modalType: "login-required",
                freeFeatureLoggedInRequiredHook: "player-inclusion",
              }),
            );
          } else {
            handleClickedPlayerSlot("include");
          }
        }}
      />
    </div>,
  );

  const handleRemovePlayer = (
    type: "include" | "exclude",
    resourceId: number,
  ) => {
    if (type === "include") {
      dispatch(removeIncludedPlayer(resourceId));
    } else {
      dispatch(removeExcludedPlayer(resourceId));
    }
  };

  const excludedPlayerSlots = excludedPlayers.map((p) => (
    <FiltersSidebarPlayerSlot
      onClick={() => handleClickedPlayerSlot("exclude", p.resourceId)}
      player={p}
    />
  ));

  excludedPlayerSlots.push(
    <div className="relative">
      <FiltersSidebarPlayerSlot
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!user.uuid && excludedPlayerSlots.length > 0) {
            dispatch(
              showModal({
                modalType: "login-required",
                freeFeatureLoggedInRequiredHook: "player-exclusion",
              }),
            );
          } else {
            handleClickedPlayerSlot("exclude");
          }
        }}
      />
    </div>,
  );

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const sbcIndex = sbc ? sbcs.findIndex((item) => item.id === sbc.id) : -1;

  const styleTitle = (
    input: string,
    highlightClass: string = "text-dark-secondary",
  ) => {
    const words = input.trim().split(" ");
    if (words.length === 1)
      return <span className="text-dark-tertiary">{words[0]}</span>;

    const first = words.shift();
    const rest = words.join(" ");

    return (
      <>
        <span className="text-dark-tertiary">{first}</span>{" "}
        <span className={highlightClass}>{rest}</span>
      </>
    );
  };

  const handlePaidActionClicked = (action: () => void) => {
    if (canUseClubImport) {
      action();
    } else {
      dispatch(
        showModal({
          modalType: "premium-feature",
          premiumFeatureHook: "solve-sbc-with-club",
        }),
      );
    }
  };

  return (
    <SbcSidePanel isOnSolutionsPage={isOnSolutionsViewPage}>
      {showImporting && (
        <NowImporting
          onClickDone={() => setShowImporting(false)}
          onClickCancel={() => setShowImporting(false)}
        />
      )}
      <FiltersActive renderHorizontally={overflowXTrue} />
      {showReward && sbc && (
        <SectionList header="Reward & Requirements" accordion isOpen={false}>
          <GetCardSBC
            key={sbc.id}
            sbc={sbc}
            index={sbcIndex}
            isOnSolutionsViewPage={isOnSolutionsViewPage}
            disableToggle={true}
          />
        </SectionList>
      )}
      <SectionList locked header="Solve with">
        <div
          className={cn(
            "m-4 bg-panel-middleground rounded-lg",
            canUseClubImport && "cursor-pointer",
          )}
        >
          <Divider />
          <SectionList.Item title="Scope">
            <FiltersPlayersFrom
              playersFrom={playersFrom}
              onClick={(playersFrom) => {
                handlePaidActionClicked(() =>
                  dispatch(updateIncludePlayersFrom(playersFrom)),
                );
              }}
            />
          </SectionList.Item>
        </div>
        <p className="text-dark-tertiary text-xs p-2">
          We're having issues with club import. Please visit the beta site to
          import your club.
        </p>
        <div className="flex flex-row w-full gap-x-2 px-6 mb-6">
          <div className="flex-1">
            <Button
              onClick={() =>
                handlePaidActionClicked(() =>
                  initImport().then(() => {
                    dispatch(setIsCurrentlyImporting(true));
                    window.open(
                      "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
                      "_blank",
                    );
                    setTimeout(() => setShowImporting(true), 1000);
                  }),
                )
              }
              variant="secondary"
              label="Import Squad"
              large
            />
          </div>
          <div className="flex-1">
            <Button
              onClick={() => {
                navigate("/import");
              }}
              variant="tertiary"
              label="Import Tutorial"
              large
              icon={importTutorialArrow}
            />
          </div>
        </div>
      </SectionList>
      <SectionList locked header="Filters" accordion>
        <div className="m-4 bg-panel-middleground rounded-lg">
          <SectionList.Item title="Rating">
            <RangeFilter
              onChangeMin={(v) => {
                handlePaidActionClicked(() => dispatch(updateMinRating(v)));
              }}
              onChangeMax={(v) => {
                handlePaidActionClicked(() => dispatch(updateMaxRating(v)));
              }}
              minValue={minRating}
              maxValue={maxRating}
              isTitle={false}
              isOnSbcFilterSidebar={true}
            />
          </SectionList.Item>
          <Divider />
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Only Use Untradeables")
                : "Only Use Untradeables"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/untradeable.png`}
                alt="untradeable"
              />
            }
            locked={!canUseClubImport}
            value={useUntradeablesOnly}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(updateUseUntradeablesOnly(!useUntradeablesOnly)),
              )
            }
          />
        </div>
        <div className="m-4 bg-panel-middleground rounded-lg">
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Prioritise SBC Storage")
                : "Prioritise SBC Storage"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/sbc-storage.png`}
                alt="sbc storage"
              />
            }
            locked={!canUseClubImport}
            value={prioritizeSbcStorage}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(updatePrioritizeSbcStorage(!prioritizeSbcStorage)),
              )
            }
          />
          <Divider />
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Prioritise Duplicates")
                : "Prioritise Duplicates"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/duplicate.png`}
                alt="duplicate"
              />
            }
            locked={!canUseClubImport}
            value={prioritizeDuplicates}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(updatePrioritizeDuplicates(!prioritizeDuplicates)),
              )
            }
          />
          <Divider />
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Prioritise Transfer List")
                : "Prioritise Transfer List"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/transfer-list.png`}
                alt="transfer list"
              />
            }
            value={includeTransferlist}
            locked={!canUseClubImport}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(updateIncludeTransferlist(!includeTransferlist)),
              )
            }
          />
        </div>
        <div className="m-4 bg-panel-middleground rounded-lg">
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Exclude SBC Players")
                : "Exclude SBC Players"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/sbc-player.png`}
                alt="sbc players"
              />
            }
            locked={!canUseClubImport}
            value={excludeSbcPlayers}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(updateExcludeSbcPlayers(!excludeSbcPlayers)),
              )
            }
          />
          <Divider />
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Exclude Objectives")
                : "Exclude Objectives"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/season-point.png`}
                alt="season-point"
              />
            }
            locked={!canUseClubImport}
            value={excludeObjectivePlayers}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(
                  updateExcludeObjectivePlayers(!excludeObjectivePlayers),
                ),
              )
            }
          />
          <Divider />
          <SectionToggle
            title={
              canUseClubImport
                ? styleTitle("Exclude Active Squad")
                : "Exclude Active Squad"
            }
            icon={
              <img
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/active-squad.png`}
                alt="active squad"
              />
            }
            locked={!canUseClubImport}
            value={excludeActiveSquad}
            onChange={() =>
              handlePaidActionClicked(() =>
                dispatch(updateExcludeActiveSquad(!excludeActiveSquad)),
              )
            }
          />
        </div>
      </SectionList>
      <SectionList
        header="Player Specific"
        closedClassName="rounded-b-lg"
        accordion
        locked={!canUseClubImport && includedPlayers.length > 0}
      >
        <IncludeExcludeSection
          type="include"
          players={includedPlayers}
          icon={roundGreenCheckmarkIcon}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          onAdd={handleAddPlayer}
          onRemove={handleRemovePlayer}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isSearching={isSearching}
          result={result}
          showAll={showAllIncluded}
          setShowAll={setShowAllIncluded}
          setShowAllSectionType={setShowAllSectionType}
          isMobile={isMobile}
        />
        <IncludeExcludeSection
          type="exclude"
          players={excludedPlayers}
          icon={roundRedMinusIcon}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          onAdd={handleAddPlayer}
          onRemove={handleRemovePlayer}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isSearching={isSearching}
          result={result}
          showAll={showAllExcluded}
          setShowAll={setShowAllExcluded}
          setShowAllSectionType={setShowAllSectionType}
          isMobile={isMobile}
        />
      </SectionList>
      <IncludedExcludedPlayers
        isOpen={showAllSection}
        onClose={() => setShowAllSectionType(null)}
        players={includedPlayers}
        excludedPlayers={excludedPlayers}
        onPlayerClick={(id) =>
          showAllSectionType && handleRemovePlayer(showAllSectionType, id)
        }
        isMobile={isMobile}
        type={showAllSectionType}
      />
    </SbcSidePanel>
  );
};

export default SBCFiltersSidebar;
